import React from 'react';

type LwLegacyTableProps = {
  children: React.ReactNode;
  footer?: React.ReactNode;
  presentation?: never;
  density?: never;
  caption?: never;
  ariaLabel?: never;
  toolbar?: never;
};

type LwCanonicalTableProps = {
  children: React.ReactNode;
  footer?: React.ReactNode;
  presentation: 'canonical';
  density?: 'comfortable' | 'compact';
  caption?: React.ReactNode;
  ariaLabel?: string;
  /** Labels and focuses a read-only table's horizontal scroll region. */
  scrollRegionLabel?: string;
  toolbar?: React.ReactNode;
};

export type LwTableProps = LwLegacyTableProps | LwCanonicalTableProps;

/** Themed table scaffold: the `.panel` card + `.ptable` element + optional `.foot`. */
export function LwTable(props: LwTableProps) {
  if (props.presentation !== 'canonical') {
    return (
      <div className="panel">
        <table className="ptable">{props.children}</table>
        {props.footer && <div className="foot">{props.footer}</div>}
      </div>
    );
  }

  const density = props.density ?? 'comfortable';
  return (
    <div className="lw-table" data-density={density}>
      {props.toolbar}
      <div
        className="lw-table__scroll"
        role={props.scrollRegionLabel ? 'region' : undefined}
        aria-label={props.scrollRegionLabel}
        tabIndex={props.scrollRegionLabel ? 0 : undefined}
      >
        <table aria-label={props.ariaLabel}>
          {props.caption && <caption>{props.caption}</caption>}
          {props.children}
        </table>
      </div>
      {props.footer && <div className="lw-table__foot">{props.footer}</div>}
    </div>
  );
}

export type LwSortDirection = 'ascending' | 'descending' | false;

export interface LwSortableHeaderProps {
  label: string;
  direction?: LwSortDirection;
  onSort: () => void;
  align?: 'left' | 'right';
  className?: string;
}

/** Sortable table heading: only the active heading owns aria-sort. */
export function LwSortableHeader({
  label,
  direction = false,
  onSort,
  align = 'left',
  className
}: LwSortableHeaderProps) {
  const ariaSort = direction || undefined;
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`${align === 'right' ? 'is-right' : ''}${className ? ` ${className}` : ''}`.trim() || undefined}
    >
      <button type="button" onClick={onSort}>
        <span>{label}</span>
        <span className="lw-table__sort" aria-hidden="true">
          {direction === 'ascending' ? '↑' : direction === 'descending' ? '↓' : '↕'}
        </span>
      </button>
    </th>
  );
}
